import { ItemCreatedUpdatedInfoView } from "@/components/views/ItemCreatedUpdatedInfoView";
import { resolveFormValidationError, setErrorsToForm } from "@/utils/formUtils";
import { regexPatterns, validations } from "@/utils/validationUtils";
import { Col, Form, Input, Modal, Row } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useCreateVehicleColor, useUpdateVehicleColor } from "../../../../../common/api/queries/vehicleColor";
import HiddenInput from "../../../../../common/components/form/components/HiddenInput";
import { ModalSizes, rowGutter } from "../../../../../common/constants";
import { CertificateVehicleColor, CreateUpdateCertificateVehicleColor } from "../../types";

interface Props {
  color?: CertificateVehicleColor;
  open: boolean;
  onFormCancel: () => void;
}

export const VehicleColorForm = ({ color, open, onFormCancel }: Props) => {
  const { t } = useTranslation();

  const createVehicleColor = useCreateVehicleColor();
  const updateVehicleColor = useUpdateVehicleColor();

  const [form] = Form.useForm<CreateUpdateCertificateVehicleColor>();

  useEffect(() => {
    if (open && color) {
      form.setFieldsValue(color);
    }
  }, [color, open, form]);

  const handleFormSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        if (color) {
          updateVehicleColor.mutate(
            { id: color.id, object: values },
            {
              onSuccess: onFormCancel,
              onError: (error) => setErrorsToForm(form, "vehicleColor.attrs", error.violations),
            },
          );
        } else {
          createVehicleColor.mutate(values, {
            onSuccess: onFormCancel,
            onError: (error) => setErrorsToForm(form, "vehicleColor.attrs", error.violations),
          });
        }
      })
      .catch(resolveFormValidationError);
  };

  return (
    <Modal
      width={ModalSizes.MEDIUM}
      open={open}
      title={color ? t("vehicleColor.titles.updateColor") : t("vehicleColor.titles.createColor")}
      okText={t("common.save")}
      cancelText={t("common.cancel")}
      confirmLoading={createVehicleColor.isPending || updateVehicleColor.isPending}
      afterClose={() => form.resetFields()}
      onOk={handleFormSubmit}
      onCancel={onFormCancel}
      mask={{ closable: false }}
    >
      <ItemCreatedUpdatedInfoView className="margin-bottom-small" item={color} />

      <Form form={form} layout="vertical" name="vehicleColorForm">
        <HiddenInput name="optimisticLockVersion" />

        <Row gutter={rowGutter}>
          <Col span={12}>
            <Form.Item
              name="value"
              label={t("vehicleColor.attrs.value")}
              rules={[
                validations.notNull,
                validations.size(1, 255),
                validations.pattern(regexPatterns.vehicleColorValueRegex),
              ]}
            >
              <Input />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="certValue"
              label={t("vehicleColor.attrs.certValue")}
              rules={[
                validations.notNull,
                validations.size(1, 255),
                validations.pattern(regexPatterns.vehicleColorCertValueRegex),
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={rowGutter}>
          <Col span={8}>
            <Form.Item
              name="komunalnaValue"
              label={t("vehicleColor.attrs.komunalnaValue")}
              rules={[validations.size(1, 255)]}
            >
              <Input />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              name="kooperativaValue"
              label={t("vehicleColor.attrs.kooperativaValue")}
              rules={[validations.size(1, 255)]}
            >
              <Input />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item name="uniqaValue" label={t("vehicleColor.attrs.uniqaValue")} rules={[validations.size(1, 255)]}>
              <Input />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};
